import {NetworkTypeEnum, ProvidersMedicalGuide} from 'domain/usecases';
import {PlanTypeEnum} from 'presentation/constants/bookingTypesEnum';
import {useGuideStore} from 'presentation/modules/guide/shared/store/guideStore';
import {SortOrderType} from 'presentation/modules/guide/shared/store/guideStore/types';
import {NetworkTypeNameEnum} from 'presentation/modules/guide/shared/ui/components/NetworkInput/constants';
import {matchesProviderName} from 'presentation/modules/guide/shared/utils/matchesProviderName';
import {useQueryParams} from 'presentation/pages/private';
import {useMemo} from 'react';
import {useHealthProvidersQuery} from '../../queries/useHealthProvidersQuery';
import {UseHealthProvidersQueryParams} from '../../queries/useHealthProvidersQuery/types';
import {IdentifierTelehealthEnum} from '../../ui/pages/HealthGuide/types';
import {GroupedProvidersByNetworkProps} from './types';

export function useFilteredHealthProviders(params: UseHealthProvidersQueryParams) {
	const {SEARCH_PARAMS} = useQueryParams();
	const {networkType, districtNames, orderBy, query} = SEARCH_PARAMS;

	const searchByProviderName = useGuideStore(state => state.searchByProviderName);

	const {
		data: allProviders = [],
		refetch: refetchProvidersQuery,
		isFetching,
		isError: providersError,
	} = useHealthProvidersQuery(params);

	const isError = providersError;

	const providerName = searchByProviderName[PlanTypeEnum.HEALTH] || query;
	const currentSortOrder = orderBy as SortOrderType;

	const isTelemedicine = (provider: ProvidersMedicalGuide) =>
		provider?.tipoRede === NetworkTypeEnum.SELF_NETWORK &&
		provider?.nmBairro === IdentifierTelehealthEnum.DIGITAL_TELECONSULTATION;

	const isSelfNetwork = (provider: ProvidersMedicalGuide) =>
		provider?.tipoRede === NetworkTypeEnum.SELF_NETWORK &&
		provider?.nmBairro !== IdentifierTelehealthEnum.DIGITAL_TELECONSULTATION;

	const isAccreditedNetwork = (provider: ProvidersMedicalGuide) =>
		provider?.tipoRede === NetworkTypeEnum.ACCREDITED_NETWORK;

	const sortProviders = (
		providers: ProvidersMedicalGuide[],
		order: SortOrderType,
	): ProvidersMedicalGuide[] => {
		if (!order || order === SortOrderType.NONE) return providers;
		return [...providers].sort((a, b) => {
			const nameA = (a?.nmFantasia || a?.nmLocal || a?.nmPrestador || '').trim();
			const nameB = (b?.nmFantasia || b?.nmLocal || b?.nmPrestador || '').trim();

			const compare = nameA.localeCompare(nameB, 'pt-BR', {sensitivity: 'base'});
			return order === SortOrderType.DESC ? -compare : compare;
		});
	};

	const filterByDistrict = (list: ProvidersMedicalGuide[]): ProvidersMedicalGuide[] => {
		if (!districtNames?.trim()) return list;

		const selectedDistrictsArray = districtNames
			.split(',')
			.map(b => b.toLowerCase().trim())
			.filter(Boolean);

		return list.filter(provider => {
			if (isTelemedicine(provider)) return true;

			const providerDistricts =
				provider?.nmBairro?.split(',').map(b => b.toLowerCase().trim()) ?? [];
			const providerCities =
				provider?.nmCidade?.split(',').map(c => c.toLowerCase().trim()) ?? [];

			return selectedDistrictsArray.some(bairroNorm => {
				if (providerDistricts.includes(bairroNorm)) return true;

				return providerDistricts.some((d, i) => {
					const cidade = providerCities[i] ?? providerCities[0] ?? '';
					return bairroNorm === `${d} - ${cidade}`;
				});
			});
		});
	};

	const filterList = (list: ProvidersMedicalGuide[]): ProvidersMedicalGuide[] => {
		let result = list;

		if (providerName) {
			result = result.filter(
				provider =>
					matchesProviderName(provider?.nmPrestador, providerName) ||
					matchesProviderName(provider?.nmFantasia, providerName) ||
					matchesProviderName(provider?.nmLocal, providerName),
			);
		}

		result = filterByDistrict(result);
		return sortProviders(result, currentSortOrder);
	};

	const filteredProviders = useMemo((): GroupedProvidersByNetworkProps => {
		const grouped: GroupedProvidersByNetworkProps = {
			telemedicina: [],
			redePropria: [],
			redeCredenciada: [],
		};

		const isGroupedResponse = allProviders.some(provider => provider?.tipoRede != null);

		if (!isGroupedResponse) {
			grouped.redeCredenciada = filterList(allProviders);
			return grouped;
		}

		const filtered = allProviders.filter(provider => {
			if (isTelemedicine(provider)) return true;
			if (networkType === NetworkTypeNameEnum.SELF_NETWORK) return isSelfNetwork(provider);
			if (networkType === NetworkTypeNameEnum.ACCREDITED_NETWORK)
				return isAccreditedNetwork(provider);
			return true;
		});

		grouped.telemedicina = filterList(filtered.filter(isTelemedicine));
		grouped.redePropria = filterList(filtered.filter(isSelfNetwork));
		grouped.redeCredenciada = filterList(filtered.filter(isAccreditedNetwork));

		return grouped;
	}, [allProviders, providerName, networkType, districtNames, currentSortOrder]);

	return {
		data: filteredProviders,
		allProviders,
		isFetching,
		isError,
		refetchProvidersQuery,
	};
}
